/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  assetPrefix,
  PaymentMethodCode,
  type Promotion,
} from '@ahm/api-wrappers-core';
import { toast } from '@ahm/react-ui';
import { Avatar, Box, Center, Group, rem, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { usePathname } from 'next/navigation';
import React, { useMemo } from 'react';
import { CircleCheckbox } from '@/components/circle-checkbox';
import { ModalName, showModal } from '@/components/modals';
import { ROUTES } from '@/config/router';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface VoucherTicketProps {
  listVoucher: Promotion[];
  voucher: Promotion;
  selectedVoucherIds: string[];
  toggleVoucher: (voucher: Promotion, allVouchers: Promotion[]) => void;
}

const VoucherTicket: React.FC<VoucherTicketProps> = ({
  listVoucher,
  voucher,
  selectedVoucherIds,
  toggleVoucher,
}) => {
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const { estimatedData, paymentMethod } = useBookingStore((state) => state);

  const convertEndTime = (timestamp: number) =>
    dayjs(timestamp * 1000).format('DD/MM, HH:mm');

  const estimateError = useMemo(() => {
    const discountDetails = (estimatedData?.data as any)?.discount_details;
    let errorFromEstimate = null;

    if (Array.isArray(discountDetails)) {
      const found = discountDetails.find(
        (d: any) => d.promo_code_id === voucher._id
      );
      if (found && !found.is_valid) {
        errorFromEstimate = found.error_message || null;
      }
    }

    return errorFromEstimate || null;
  }, [voucher._id, estimatedData]);

  const voucherError = useMemo(() => {
    let errorInVoucher = null;

    if (!voucher.valid && (voucher as any).error_message) {
      errorInVoucher = (voucher as any).error_message;
    }

    return estimateError || errorInVoucher || null;
  }, [voucher, estimateError]);

  const isValidVoucher = voucher.valid && !voucherError;

  const selectedVouchers = useMemo(
    () => listVoucher.filter((v) => selectedVoucherIds.includes(v._id)),
    [listVoucher, selectedVoucherIds]
  );

  // Business rules :
  // 1. 1 category only choose 1 voucher
  // 2. Incompatible with selected voucher
  // 3. Nationwide: only choose 1 voucher if payment method !== balance
  const categoryHasSelection =
    !!voucher.category &&
    selectedVouchers.some(
      (v) => v.category === voucher.category && v._id !== voucher._id
    );

  const isIncompatibleWithSelected = selectedVouchers.some((v) =>
    (v as any).incompatible_with?.includes(voucher.category || '')
  );

  const isNationwide = pathname.includes(ROUTES.nationwide);

  const isStackingCouponNationwide =
    isNationwide &&
    paymentMethod &&
    paymentMethod.code.toUpperCase() === PaymentMethodCode.Balance;

  const isPreventedByNationwideRule =
    isNationwide &&
    !isStackingCouponNationwide &&
    selectedVouchers.length > 0 &&
    !selectedVoucherIds.includes(voucher._id);

  const isPreventedByBusinessRule =
    categoryHasSelection ||
    isIncompatibleWithSelected ||
    isPreventedByNationwideRule;

  const isDisabled = !isValidVoucher || isPreventedByBusinessRule;

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (isPreventedByBusinessRule) {
      toast.error({
        title:
          'Không thể thêm mã khác do đơn hàng đã đạt giới hạn số lượng mã có thể áp dụng.',
        withIcon: true,
        withCloseButton: true,
      });
      return;
    }
    toggleVoucher(voucher, listVoucher);
  };

  const onOpenDetailModal = () => {
    showModal(
      ModalName.PromotionDetail,
      {
        promo_id: voucher._id,
        valid: isShowToggle && !isPreventedByBusinessRule,
      },
      {
        centered: true,
        fullScreen: isMobile,
        withCloseButton: false,
        closeOnClickOutside: true,
        closeOnEscape: true,
        styles: { body: { height: isMobile ? '100%' : 641 } },
      }
    );
  };

  const isShowToggle = useMemo(() => {
    // Show toggle when voucher is statically valid and no conflicts (ignore API errors)
    return voucher.valid || estimateError;
  }, [voucher.valid, estimateError]);

  return (
    <Group
      className="voucher-ticket"
      justify="flex-start"
      align="stretch"
      w="100%"
      mih={rem(50)}
      gap={rem(8)}
      px={rem(16)}
      bg="white"
      style={{
        borderRadius: rem(8),
        boxShadow: '0 2px 4px 0 rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={onOpenDetailModal}
    >
      <Center>
        <Avatar
          src={
            voucher.icon_url ||
            `${assetPrefix}/static/images/default-promo.webp`
          }
          alt={voucher.description || 'Voucher Icon'}
          w={rem(50)}
          h={rem(50)}
          radius={rem(8)}
          variant="filled"
          style={{
            border: '1px solid var(--mantine-color-neutral-0)',
            filter: isDisabled ? 'grayscale(100%)' : 'none',
          }}
        />
      </Center>

      {/* Vertical Divider */}
      <div className="flex flex-col items-center justify-center gap-1">
        <div
          className="rounded-full bg-neutral-00"
          style={{ marginTop: '-4px', height: '10px', width: '10px' }}
        />
        <div className="h-full min-h-[40px] border border-dashed border-neutral-00" />
        <div
          className="rounded-full bg-neutral-00"
          style={{ marginBottom: '-4px', height: '10px', width: '10px' }}
        />
      </div>

      {/* Voucher Info */}
      <Group
        justify="space-between"
        flex={1}
        gap={rem(8)}
        wrap="nowrap"
        py={rem(8)}
      >
        <Stack gap={rem(16)} flex={1}>
          <Stack gap={rem(2)}>
            {voucher.source_name && (
              <Text
                fz={rem(10)}
                fw={500}
                lh={rem(14)}
                c="neutral.6"
                lineClamp={1}
              >
                {voucher.source_name}
              </Text>
            )}
            <Text fw={600} fz={rem(16)} lineClamp={2}>
              {voucher.description || voucher.content_vi_vn}
            </Text>
          </Stack>

          <Stack gap={0}>
            <Text fz={rem(12)} fw={500} lh={rem(16)} c="neutral.6">
              HSD: {convertEndTime(voucher.end_time)}
            </Text>
            {voucherError && (
              <Text fz={rem(10)} fw={500} lh={rem(14)} lineClamp={2} c="red.6">
                {voucherError}
              </Text>
            )}
          </Stack>
        </Stack>

        {isShowToggle ? (
          <Group
            align="center"
            flex="none"
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <CircleCheckbox
              checked={selectedVoucherIds.includes(voucher._id)}
              onChange={handleCheckboxChange}
            />
          </Group>
        ) : null}
      </Group>

      {/* Remaining Use Badge */}
      {voucher.remaining_use > 0 ? (
        <Box
          pos="absolute"
          top={0}
          right={0}
          p={rem(4)}
          bg={isDisabled ? 'neutral.5' : 'blue.7'}
          style={{ borderRadius: '0 0 0 8px' }}
        >
          <Text c="white" fz={rem(10)} fw={700} lh={rem(14)}>
            x{voucher.remaining_use}
          </Text>
        </Box>
      ) : null}
    </Group>
  );
};

export default VoucherTicket;
